import React from 'react';

// Small animated SVG illustrations for F25 deep-dive modals and onboarding.
// Code-driven by design (no Lottie/video): themable via CSS variables,
// localizable, crisp at any size. Animations are pure CSS (see explain.css);
// `prefers-reduced-motion` disables them globally there.

const Svg = ({ children, label, viewBox = '0 0 320 160' }) => (
    <svg viewBox={viewBox} role="img" aria-label={label} className="explain-illustration">
        {children}
    </svg>
);

// Small SVG text label used by the story variants of the illustrations.
const SvgLabel = ({ x, y, children, fill = 'var(--text-secondary)', anchor = 'start', delay, bold = false }) => (
    <text
        x={x} y={y}
        textAnchor={anchor}
        fill={fill}
        fontSize="10.5"
        fontWeight={bold ? 700 : 500}
        className="explain-fade"
        style={delay ? { animationDelay: delay } : undefined}
    >
        {children}
    </text>
);

// A wealth curve being bent upward by additions — "everything shapes the chart".
export const ChartGrowsIllustration = ({ label }) => (
    <Svg label={label}>
        <line x1="16" y1="136" x2="304" y2="136" stroke="var(--border)" strokeWidth="1" />
        <path
            className="explain-draw explain-draw-slow"
            d="M16,132 C80,128 120,112 176,84 C224,60 268,40 304,26"
            fill="none" stroke="var(--accent)" strokeWidth="3" strokeLinecap="round"
        />
        <path
            d="M16,132 C80,128 120,112 176,84 C224,60 268,40 304,26 L304,136 L16,136 Z"
            fill="var(--accent-bg)" stroke="none"
        />
        <g className="explain-pop" style={{ animationDelay: '0.5s' }}>
            <circle cx="108" cy="78" r="10" fill="var(--chip-income-bg)" stroke="var(--chip-income-fg)" strokeWidth="1.5" />
            <path d="M108 83 v-10 m-4 4 l4 -4 l4 4" stroke="var(--chip-income-fg)" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        </g>
        <g className="explain-pop" style={{ animationDelay: '0.9s' }}>
            <circle cx="188" cy="48" r="10" fill="var(--chip-realestate-bg)" stroke="var(--chip-realestate-fg)" strokeWidth="1.5" />
            <path d="M182 50 l6 -6 l6 6 M184 49 v5 h8 v-5" stroke="var(--chip-realestate-fg)" strokeWidth="1.5" fill="none" strokeLinejoin="round" />
        </g>
        <g className="explain-pop" style={{ animationDelay: '1.3s' }}>
            <circle cx="256" cy="22" r="10" fill="var(--chip-investment-bg)" stroke="var(--chip-investment-fg)" strokeWidth="1.5" />
            <path d="M250 26 l4 -4 l3 2 l5 -6" stroke="var(--chip-investment-fg)" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        </g>
    </Svg>
);

// Assets curve crossing the FIRE-number line; shaded independence zone after.
// Optional `texts` ({assets, target, freedom}) turns it into the labeled
// story variant used by the full-screen explainer.
export const FireCrossoverIllustration = ({ label, texts }) => (
    <Svg label={label}>
        <rect x="208" y="16" width="96" height="120" fill="var(--success-bg)" className="explain-fade" style={{ animationDelay: '1.2s' }} />
        <line x1="16" y1="136" x2="304" y2="136" stroke="var(--border)" strokeWidth="1" />
        <path
            className="explain-draw"
            d="M16,60 C100,58 200,54 304,48"
            fill="none" stroke="var(--warning)" strokeWidth="2" strokeDasharray="6 5" strokeLinecap="round"
        />
        <path
            className="explain-draw explain-draw-slow"
            d="M16,128 C90,122 160,96 208,56 C240,32 276,22 304,18"
            fill="none" stroke="var(--accent)" strokeWidth="3" strokeLinecap="round"
        />
        <circle cx="208" cy="56" r="6" fill="var(--accent)" className="explain-pop" style={{ animationDelay: '1.4s' }} />
        {texts && (
            <>
                <SvgLabel x={20} y={50} fill="var(--warning)" delay="0.6s">{texts.target}</SvgLabel>
                <SvgLabel x={20} y={118} fill="var(--accent)" delay="0.9s">{texts.assets}</SvgLabel>
                <SvgLabel x={256} y={130} fill="var(--success)" anchor="middle" delay="1.5s" bold>{texts.freedom}</SvgLabel>
            </>
        )}
    </Svg>
);

// Story variant for the Timeline explainer: three dated life events land in
// sequence, each visibly bending the wealth curve away from the dashed
// "no plan" baseline. `texts` = {e1, e2, e3} event names.
export const TimelineStoryIllustration = ({ label, texts = {} }) => (
    <Svg label={label} viewBox="0 0 320 176">
        <line x1="16" y1="150" x2="304" y2="150" stroke="var(--border)" strokeWidth="1" />
        {/* baseline without events */}
        <path
            className="explain-fade"
            style={{ animationDelay: '0.2s' }}
            d="M16,134 C110,128 210,120 304,110"
            fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeDasharray="5 5" strokeLinecap="round"
        />
        {/* with events: dip (flat purchase) → steeper (raise) → softer but still up (child) */}
        <path
            className="explain-draw explain-draw-slow"
            d="M16,134 C42,132 68,131 88,130 C96,130 98,140 106,139 C130,136 152,122 168,110 C176,104 186,92 200,84 C216,74 232,72 244,68 C266,60 288,50 304,42"
            fill="none" stroke="var(--accent)" strokeWidth="3" strokeLinecap="round"
        />
        {/* event pins, staggered to land as the curve reaches them */}
        <g className="explain-pop" style={{ animationDelay: '0.55s' }}>
            <line x1="94" y1="132" x2="94" y2="52" stroke="var(--chip-realestate-fg)" strokeWidth="1.5" />
            <circle cx="94" cy="42" r="10" fill="var(--chip-realestate-bg)" stroke="var(--chip-realestate-fg)" strokeWidth="1.5" />
            <path d="M88 44 l6 -6 l6 6 M90 43 v5 h8 v-5" stroke="var(--chip-realestate-fg)" strokeWidth="1.4" fill="none" strokeLinejoin="round" />
        </g>
        <g className="explain-pop" style={{ animationDelay: '1.05s' }}>
            <line x1="182" y1="98" x2="182" y2="52" stroke="var(--chip-income-fg)" strokeWidth="1.5" />
            <circle cx="182" cy="42" r="10" fill="var(--chip-income-bg)" stroke="var(--chip-income-fg)" strokeWidth="1.5" />
            <path d="M182 47 v-10 m-4 4 l4 -4 l4 4" stroke="var(--chip-income-fg)" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        </g>
        <g className="explain-pop" style={{ animationDelay: '1.55s' }}>
            <line x1="248" y1="66" x2="248" y2="52" stroke="var(--chip-expense-fg)" strokeWidth="1.5" />
            <circle cx="248" cy="42" r="10" fill="var(--chip-expense-bg)" stroke="var(--chip-expense-fg)" strokeWidth="1.5" />
            <circle cx="248" cy="39.5" r="2.6" fill="none" stroke="var(--chip-expense-fg)" strokeWidth="1.4" />
            <path d="M243.5 47.5 c0 -3 2 -4.5 4.5 -4.5 c2.5 0 4.5 1.5 4.5 4.5" fill="none" stroke="var(--chip-expense-fg)" strokeWidth="1.4" strokeLinecap="round" />
        </g>
        {texts.e1 && <SvgLabel x={94} y={24} anchor="middle" delay="0.7s">{texts.e1}</SvgLabel>}
        {texts.e2 && <SvgLabel x={182} y={24} anchor="middle" delay="1.2s">{texts.e2}</SvgLabel>}
        {texts.e3 && <SvgLabel x={248} y={24} anchor="middle" delay="1.7s">{texts.e3}</SvgLabel>}
    </Svg>
);

// Story variant for the Simulation explainer: many thin random runs draw in
// first, then the probability bands settle over them, then the bold median.
// `texts` = {median, band}.
export const SimulationStoryIllustration = ({ label, texts = {} }) => {
    const runs = [
        'M16,130 C70,124 130,96 190,72 C230,56 270,34 304,22',
        'M16,130 C70,120 130,104 190,88 C240,74 275,58 304,44',
        'M16,130 C75,126 135,114 195,98 C240,86 275,72 304,62',
        'M16,130 C80,128 140,120 195,110 C245,100 280,88 304,78',
        'M16,130 C80,130 145,126 200,120 C250,114 282,104 304,96',
        'M16,130 C80,132 145,132 200,130 C250,127 282,120 304,114',
        'M16,130 C80,134 150,138 205,140 C255,141 285,136 304,132',
    ];
    return (
        <Svg label={label} viewBox="0 0 320 176">
            <line x1="16" y1="156" x2="304" y2="156" stroke="var(--border)" strokeWidth="1" />
            {runs.map((d, i) => (
                <path
                    key={d}
                    className="explain-draw"
                    style={{ animationDelay: `${0.15 + i * 0.14}s`, opacity: 0.45 }}
                    d={d}
                    fill="none" stroke="var(--text-muted)" strokeWidth="1.2" strokeLinecap="round"
                />
            ))}
            {/* 90% band */}
            <path
                className="explain-fade"
                style={{ animationDelay: '1.5s' }}
                d="M16,130 C70,124 130,96 190,72 C230,56 270,34 304,22 L304,132 C285,136 255,141 205,140 C150,138 80,134 16,130 Z"
                fill="var(--accent-bg)" stroke="none"
            />
            {/* 50% band */}
            <path
                className="explain-fade"
                style={{ animationDelay: '1.7s' }}
                d="M16,130 C70,120 130,104 190,88 C240,74 275,58 304,44 L304,96 C282,104 250,114 200,120 C145,126 80,130 16,130 Z"
                fill="var(--accent-bg)" stroke="none"
            />
            <path
                className="explain-draw explain-draw-slow"
                style={{ animationDelay: '1.8s' }}
                d="M16,130 C78,126 140,112 198,102 C245,94 278,80 304,70"
                fill="none" stroke="var(--accent)" strokeWidth="3" strokeLinecap="round"
            />
            {texts.median && <SvgLabel x={300} y={62} anchor="end" fill="var(--accent)" delay="2.4s" bold>{texts.median}</SvgLabel>}
            {texts.band && <SvgLabel x={300} y={16} anchor="end" delay="2.0s">{texts.band}</SvgLabel>}
        </Svg>
    );
};

// A fan of possible futures around a median path.
export const PercentileFanIllustration = ({ label }) => (
    <Svg label={label}>
        <line x1="16" y1="136" x2="304" y2="136" stroke="var(--border)" strokeWidth="1" />
        <path d="M16,120 C110,116 210,96 304,20 L304,132 C210,124 110,126 16,124 Z" fill="var(--accent-bg)" className="explain-fade" />
        <path d="M16,121 C110,116 210,102 304,52 L304,110 C210,116 110,122 16,123 Z" fill="var(--accent-bg)" className="explain-fade" style={{ animationDelay: '0.3s' }} />
        <path
            className="explain-draw explain-draw-slow"
            d="M16,122 C110,116 210,100 304,76"
            fill="none" stroke="var(--accent)" strokeWidth="3" strokeLinecap="round"
        />
    </Svg>
);

// A dated event pin bending the projection.
export const TimelineEventIllustration = ({ label }) => (
    <Svg label={label}>
        <line x1="16" y1="136" x2="304" y2="136" stroke="var(--border)" strokeWidth="1" />
        <path
            className="explain-draw"
            d="M16,120 C60,116 110,110 160,102"
            fill="none" stroke="var(--accent)" strokeWidth="3" strokeLinecap="round"
        />
        <path
            className="explain-draw explain-draw-slow"
            d="M160,102 C210,88 260,56 304,30"
            fill="none" stroke="var(--accent)" strokeWidth="3" strokeLinecap="round" style={{ animationDelay: '0.7s' }}
        />
        <path
            d="M160,102 C210,96 260,86 304,78"
            fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeDasharray="4 5" strokeLinecap="round"
        />
        <g className="explain-pop" style={{ animationDelay: '0.6s' }}>
            <line x1="160" y1="102" x2="160" y2="48" stroke="var(--chart-2)" strokeWidth="1.5" />
            <circle cx="160" cy="40" r="9" fill="var(--chart-2)" />
            <path d="M156 40 h8 M160 36 v8" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
        </g>
    </Svg>
);

// Snapshot pins freezing ghost copies of the wealth curve at past dates,
// drifting apart from the live accent curve. Optional `texts` ({then, now})
// labels the story variant used by the full-screen explainer.
export const SnapshotsIllustration = ({ label, texts }) => (
    <Svg label={label}>
        <line x1="16" y1="136" x2="304" y2="136" stroke="var(--border)" strokeWidth="1" />
        <path
            d="M16,124 C90,118 180,104 304,86"
            fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeDasharray="5 5" strokeLinecap="round"
            className="explain-fade" style={{ animationDelay: '0.5s' }}
        />
        <path
            d="M16,124 C80,120 150,110 220,88 C255,77 285,64 304,56"
            fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeDasharray="5 5" strokeLinecap="round"
            className="explain-fade" style={{ animationDelay: '0.9s' }}
        />
        <path
            className="explain-draw explain-draw-slow"
            d="M16,124 C80,118 150,102 220,72 C255,57 285,40 304,30"
            fill="none" stroke="var(--accent)" strokeWidth="3" strokeLinecap="round"
        />
        <g className="explain-pop" style={{ animationDelay: '0.5s' }}>
            <line x1="96" y1="118" x2="96" y2="52" stroke="var(--chip-cash-fg)" strokeWidth="1.5" />
            <circle cx="96" cy="42" r="10" fill="var(--chip-cash-bg)" stroke="var(--chip-cash-fg)" strokeWidth="1.5" />
            <rect x="91" y="39" width="10" height="7" rx="1.5" fill="none" stroke="var(--chip-cash-fg)" strokeWidth="1.2" />
            <circle cx="96" cy="42.5" r="1.6" fill="var(--chip-cash-fg)" />
        </g>
        <g className="explain-pop" style={{ animationDelay: '0.9s' }}>
            <line x1="192" y1="96" x2="192" y2="52" stroke="var(--chip-cash-fg)" strokeWidth="1.5" />
            <circle cx="192" cy="42" r="10" fill="var(--chip-cash-bg)" stroke="var(--chip-cash-fg)" strokeWidth="1.5" />
            <rect x="187" y="39" width="10" height="7" rx="1.5" fill="none" stroke="var(--chip-cash-fg)" strokeWidth="1.2" />
            <circle cx="192" cy="42.5" r="1.6" fill="var(--chip-cash-fg)" />
        </g>
        {texts && (
            <>
                <SvgLabel x={96} y={24} anchor="middle" fill="var(--chip-cash-fg)" delay="0.7s">{texts.then}</SvgLabel>
                <SvgLabel x={298} y={22} anchor="end" fill="var(--accent)" delay="1.4s" bold>{texts.now}</SvgLabel>
            </>
        )}
    </Svg>
);

export const ILLUSTRATIONS = {
    chartGrows: ChartGrowsIllustration,
    fireCrossover: FireCrossoverIllustration,
    percentileFan: PercentileFanIllustration,
    timelineEvent: TimelineEventIllustration,
    snapshots: SnapshotsIllustration,
};
